/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Racking Inspection Begin Page
 * This page displays after clicking "Begin" in the Racking Inspection module
 * It shows regulatory standards and a miscellaneous observations section
 */

import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  ScrollView,
  KeyboardAvoidingView,
  ActivityIndicator,
  Animated,
  TouchableOpacity,
  TextInput,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
// Import the API utilities from components
import { submitRackingInspection } from "../../components/racking_inspection/submitRackingInspection";
import { fetchRackingInspectionQuestions } from "../../components/racking_inspection/fetchRackingInspectionQuestions";
import {
  setQuestions,
  setLoading,
  setError,
  clearQuestions,
} from "../../store/slices/rackingInspectionQuestionsSlice";
import { fetchDamageChecklistData } from "../../components/racking_inspection/fetchDamageChecklistData";
import { useToast } from "../../components/LhToastContainer";
// Import pulseCheck actions for miscellaneous observations
import {
  setObservationImage,
  setMiscImageDescription,
} from "../../store/slices/pulseCheckSlice";
import RackingInspectionActionButtons from "./components/RackingInspectionActionButtons";
import { RootState } from "../../store";
import LHRackingHeader from "./components/LHRackingHeader";
import LHSectionHeader from "../../components/LHSectionHeader";
import NewDamageForm from "../../components/racking_inspection/NewDamageForm";
import LHDataTable from "../../components/LHDataTable";
import { tableColumns } from "./data/DamageTableData";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import RegulatoryStandards, {
  RegulatoryStandardItem,
  createDefaultColumns,
} from "../../components/racking_inspection/RegulatoryStandards";
import LHRackingInspectionOptionCard from "../../components/racking_inspection/LHRackingInspectionOptionCard";
import LHMiscellaneousObservations from "../../components/LHMiscellaneousObservations.fixed.new";
import DamageImageUploadModal from "../../components/racking_inspection/DamageImageUploadModal";
import SignaturePad from "../../components/SignaturePad";
import LhButton from "../../components/LhButton";
import FontFamily from "../../constants/fonts";
import { useTheme } from "../../theme";

interface RackingInspectionBeginPageProps {
  onBackPress: () => void;
  onHomePress?: () => void;
  onSave: (status: "in-progress" | "completed") => void;
  periodWeek?: string;
  userEmail?: string;
  walkData?: any;
}

/**
 * Racking Inspection Begin Page Component
 * @param props Component properties
 * @returns RackingInspectionBeginPage component
 */
const RackingInspectionBeginPage: React.FC<RackingInspectionBeginPageProps> = ({
  onBackPress,
  onHomePress,
  onSave,
  periodWeek,
  userEmail,
  walkData,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  // Initialize toast notification system
  // Populate state from walkData when it changes
  // State for oneSubmission and selectedRecord
  const [oneSubmission, setOneSubmission] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<{ id: any } | null>(
    null
  );

  // This effect merges walkData values into regulatoryStandards/loadCapacityStandards after both are loaded
  useEffect(() => {
    if (!walkData) {
      return;
    }
    console.log("[DEBUG] walkData.RS loaded:", walkData.RS);
    console.log("[DEBUG] walkData.LC loaded:", walkData.LC);
    if (Array.isArray(walkData.RS)) {
      setRegulatoryStandards((prev) => {
        if (prev.length === 0) {
          return prev;
        }
        return prev.map((std) => {
          const match = walkData.RS.find(
            (rs: any) =>
              String(rs.QNUM) === String(std.QNUM)
          );
          if (match) {
            return {
              ...std,
              comments: match.COMMENTS ?? match.comments ?? std.comments ?? "",
              instances:
                match.INSTANCES ?? match.instances ?? std.instances ?? "",
              // Use || so that empty-string server values don't overwrite
              // locally uploaded image URLs that haven't been saved yet.
              imageUrl: std.imageUrl || match.IMG1 || match.imageUrl || "",
              imageUrl2: std.imageUrl2 || match.IMG2 || match.imageUrl2 || "",
              imageFileName: std.imageFileName || match.imageFileName || "",
              imageFileName2: std.imageFileName2 || match.imageFileName2 || "",
              description:
                match.DESC1 ?? match.description ?? std.description ?? "",
              description1:
                std.description1 || match.DESC1 || match.description1 || match.description || "",
              description2:
                std.description2 || match.DESC2 || match.description2 || "",
            };
          }
          return std;
        });
      });
    }
    if (Array.isArray(walkData.LC)) {
      setLoadCapacityStandards((prev) => {
        if (prev.length === 0) {
          return prev;
        }
        return prev.map((std) => {
          const match = walkData.LC.find(
            (lc: any) =>
              String(lc.QNUM) === String(std.QNUM)
          );
          if (match) {
            return {
              ...std,
              comments: match.COMMENTS ?? match.comments ?? std.comments ?? "",
              instances:
                match.INSTANCES ?? match.instances ?? std.instances ?? "",
              // Use || so that empty-string server values don't overwrite
              // locally uploaded image URLs that haven't been saved yet.
              imageUrl: std.imageUrl || match.IMG1 || match.imageUrl || "",
              imageUrl2: std.imageUrl2 || match.IMG2 || match.imageUrl2 || "",
              imageFileName: std.imageFileName || match.imageFileName || "",
              imageFileName2: std.imageFileName2 || match.imageFileName2 || "",
              description:
                match.DESC1 ?? match.description ?? std.description ?? "",
              description1:
                std.description1 || match.DESC1 || match.description1 || match.description || "",
              description2:
                std.description2 || match.DESC2 || match.description2 || "",
            };
          }
          return std;
        });
      });
    }
  }, [walkData]);

  // This effect sets misc, signature, etc. (selectedRS/LC are now handled by the synchronization useEffect)
  useEffect(() => {
    console.log(
      "RackingInspectionBeginPage - useEffect for walkData triggered, walkData:",
      walkData
    );
    if (walkData) {
      // Note: selectedRS and selectedLC are now handled by the synchronization useEffect above
      // This prevents the reset issue that was happening before
      
      // For damage data, only store damages whose UID matches the walkData.uid from walkData.DM
      // Filter by UID match instead of Status
      if (Array.isArray(walkData.DM) && walkData.DM.length > 0) {
        // Get the UID from walkData - it should be available from the component props
        const currentWalkUID = walkData.uid;
        console.log(
          "RackingInspectionBeginPage - Current walk UID:",
          currentWalkUID
        );
        console.log("RackingInspectionBeginPage - Full walkData:", walkData);

        if (currentWalkUID) {
          const matchingUIDDamages = walkData.DM.filter(
            (damage: any) => damage.UID === currentWalkUID
          );
          console.log("matchingUIDDamages:", matchingUIDDamages);
          setAllUserDamageData(matchingUIDDamages);
          console.log(
            "RackingInspectionBeginPage - Filtered damages by UID match from walkData.DM:",
            matchingUIDDamages.length,
            "out of",
            walkData.DM.length
          );
        } else {
          console.log(
            "RackingInspectionBeginPage - No UID found in walkData, showing all damages as fallback"
          );
          console.log(
            "RackingInspectionBeginPage - Available UIDs in DM:",
            walkData.DM.map((d: any) => d.UID)
          );
          // Fallback: if no UID in walkData, show all damages (you may want to adjust this behavior)
          setAllUserDamageData(walkData.DM);
        }
      }
      console.log(
        "RackingInspectionBeginPage - allUserDamageData set to:",
        allUserDamageData
      );
      // Populate misc observations to Redux
      if (walkData.MSC) {
        // Set images
        if (walkData.MSC.IMG1) {
          dispatch(
            setObservationImage({
              index: 0,
              image: {
                imageUrl: walkData.MSC.IMG1,
                fileName: "misc_image_1.jpg",
              },
            })
          );
        }
        if (walkData.MSC.IMG2) {
          dispatch(
            setObservationImage({
              index: 1,
              image: {
                imageUrl: walkData.MSC.IMG2,
                fileName: "misc_image_2.jpg",
              },
            })
          );
        }
        // Set descriptions
        dispatch(
          setMiscImageDescription({
            description1: walkData.MSC.DESC1 || "",
            description2: walkData.MSC.DESC2 || "",
          })
        );
      }
      // Populate signature from walkData if available
      if (walkData.SIGN && walkData.SIGN.Signature) {
        setSignature({
          fileName: "signature.png",
          imageUrl: walkData.SIGN.Signature,
        });
      }

      // Check for status in multiple possible locations and formats
      // From the API response sample, we need to check:
      // 1. walkData.Status - top level status
      // 2. walkData.status - lowercase variant
      // 3. walkData.rawData?.Status - nested in rawData
      // 4. walkData.action - might contain status text
      // 5. SIGN.Status - signature status might indicate completed inspection
      let statusFound = false;

      // First check the top-level Status field (most likely from API)
      if (walkData.Status !== undefined) {
        const statusValue = Number(walkData.Status);
        console.log(
          "RackingInspectionBeginPage - Using walkData.Status:",
          walkData.Status,
          "statusValue:",
          statusValue
        );
        setInspectionStatus(statusValue === 1 ? "completed" : "in-progress");
        statusFound = true;
      }
      // Check lowercase status (API might return lowercase)
      else if (walkData.status !== undefined) {
        const statusValue = Number(walkData.status);
        console.log(
          "RackingInspectionBeginPage - Using walkData.status:",
          walkData.status,
          "statusValue:",
          statusValue
        );
        setInspectionStatus(statusValue === 1 ? "completed" : "in-progress");
        statusFound = true;
      }
      // Check if status is nested in rawData (from table)
      else if (walkData.rawData && walkData.rawData.Status !== undefined) {
        const statusValue = Number(walkData.rawData.Status);
        console.log(
          "RackingInspectionBeginPage - Using walkData.rawData.Status:",
          walkData.rawData.Status,
          "statusValue:",
          statusValue
        );
        setInspectionStatus(statusValue === 1 ? "completed" : "in-progress");
        statusFound = true;
      }
      // Check if the action field contains status text
      else if (walkData.action && typeof walkData.action === "string") {
        const actionLower = walkData.action.toLowerCase();
        console.log(
          "RackingInspectionBeginPage - Using walkData.action:",
          walkData.action
        );
        if (actionLower.includes("complete")) {
          setInspectionStatus("completed");
          statusFound = true;
        } else if (actionLower.includes("progress")) {
          setInspectionStatus("in-progress");
          statusFound = true;
        }
      }
      // Check signature status as a last resort
      else if (walkData.SIGN && walkData.SIGN.Status !== undefined) {
        const signStatus = Number(walkData.SIGN.Status);
        console.log(
          "RackingInspectionBeginPage - Using walkData.SIGN.Status:",
          walkData.SIGN.Status,
          "statusValue:",
          signStatus
        );
        if (signStatus === 1) {
          setInspectionStatus("completed");
          statusFound = true;
        }
      }

      // Log if status could not be determined
      if (!statusFound) {
        console.log(
          "RackingInspectionBeginPage - STATUS NOT FOUND in walkData:",
          JSON.stringify(walkData, null, 2)
        );
        // Default to in-progress if we can't determine the status
        setInspectionStatus("in-progress");
      }

      // If status is 0, set oneSubmission and selectedRecord
      if (typeof walkData.SIGN?.Status !== "undefined" && walkData.uid) {
        setOneSubmission(true);
        setSelectedRecord({ id: walkData.uid });
      }
    }
  }, [walkData]);
  const toast = useToast();

  // Get the selected location from Redux store
  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );

  // Redirect to RackingInspection dashboard when location changes while on this page
  const initialLocationIdRef = useRef<string | undefined>(selectedLocation?.id);
  const onBackPressRef = useRef(onBackPress);
  useEffect(() => { onBackPressRef.current = onBackPress; }, [onBackPress]);
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    const initialId = initialLocationIdRef.current;
    if (
      !hasRedirectedRef.current &&
      initialId &&
      selectedLocation?.id &&
      initialId !== selectedLocation.id
    ) {
      hasRedirectedRef.current = true;
      onBackPressRef.current();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id]);

  // Progress animation reference
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const progress = useRef(new Animated.Value(0.4)).current;

  const dispatch = useDispatch();
  // Redux state for questions
  const rackingInspectionQuestions = useSelector(
    (state: RootState) =>
      (state as any).rackingInspectionQuestions?.questions || []
  );
  const questionsLoading = useSelector(
    (state: RootState) => (state as any).rackingInspectionQuestions?.loading
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const questionsError = useSelector(
    (state: RootState) => (state as any).rackingInspectionQuestions?.error
  );

  // Get walk observations from Redux to check status
  const walkObservations = useSelector(
    (state: RootState) => (state as any).rackingWalkObservations?.walkObservations || []
  );

  // State for standards, loaded from API questions
  const [regulatoryStandards, setRegulatoryStandards] = useState<
    RegulatoryStandardItem[]
  >([]);
  const [loadCapacityStandards, setLoadCapacityStandards] = useState<
    RegulatoryStandardItem[]
  >([]);
  const [damageQuestions, setDamageQuestions] = useState<any[]>([]);

  // State for selected RS and LC in required format
  const [selectedRS, setSelectedRS] = useState<any[]>([]);
  const [selectedLC, setSelectedLC] = useState<any[]>([]);

  // Load questions from API if not in Redux.
  // NOTE: Do NOT include rackingInspectionQuestions in deps — when the API returns an
  // empty array, dispatch(setQuestions([])) creates a new [] reference each time,
  // which would re-trigger this effect and cause an infinite loading loop on iOS.
  // Running once on mount is sufficient: if questions are already in Redux we skip,
  // otherwise we fetch them once.
  useEffect(() => {
    if (rackingInspectionQuestions && rackingInspectionQuestions.length > 0) {
      return; // Already loaded — skip fetch
    }
    const loadQuestions = async () => {
      try {
        dispatch(setLoading(true));
        
        // Check if this is a completed inspection to skip org context params
        // For completed inspections, we want the original question set (frozen at completion time)
        // For new/in-progress inspections, we want questions filtered by current location org context
        
        let isCompletedInspection = false;
        const walkRecordId = walkData?.uid ? String(walkData.uid) : undefined;

        // First preference: use explicit status from selected walk payload.
        const explicitStatus =
          walkData?.Status ?? walkData?.status ?? walkData?.rawData?.Status;

        if (
          explicitStatus !== undefined &&
          explicitStatus !== null &&
          explicitStatus !== ""
        ) {
          const normalizedStatus = Number(explicitStatus);
          if (!Number.isNaN(normalizedStatus)) {
            isCompletedInspection = normalizedStatus === 1;
          } else if (typeof explicitStatus === "string") {
            isCompletedInspection = explicitStatus
              .toLowerCase()
              .includes("complete");
          }

          console.log(
            "[Racking Inspection] Using walkData status to classify inspection - status:",
            explicitStatus,
            "isCompletedInspection:",
            isCompletedInspection
          );
        } else if (walkData?.uid && walkObservations && walkObservations.length > 0) {
          // Fallback: match by UID and started-by to avoid collisions when UID repeats.
          const targetStartedBy =
            walkData?.walkStartedBy ||
            walkData?.["Walk Started BY"] ||
            walkData?.rawData?.["Walk Started BY"] ||
            walkData?.rawData?.WALK_STARTED_BY;

          const matchingWalk = walkObservations.find((walk: any) => {
            const walkUID = walk.UID || walk.RSUID || walk.uid;
            if (walkUID !== walkData.uid) {
              return false;
            }

            if (!targetStartedBy) {
              return true;
            }

            const walkStartedBy =
              walk["Walk Started BY"] || walk.WALK_STARTED_BY;

            return (
              String(walkStartedBy || "").toLowerCase() ===
              String(targetStartedBy).toLowerCase()
            );
          });

          if (matchingWalk) {
            const walkStatus =
              matchingWalk.Status !== undefined
                ? matchingWalk.Status
                : matchingWalk.WalkStatus;

            isCompletedInspection = walkStatus === 1 || walkStatus === "1";

            console.log(
              "[Racking Inspection] Fallback matched walk observation - UID:",
              walkData.uid,
              "StartedBy:",
              targetStartedBy,
              "Status:",
              walkStatus,
              "isCompletedInspection:",
              isCompletedInspection
            );
          } else {
            console.log(
              "[Racking Inspection] No matching walk found for UID/StartedBy:",
              walkData.uid,
              targetStartedBy
            );
          }
        } else {
          console.log(
            "[Racking Inspection] New inspection - no UID or walk observations"
          );
        }
        
        console.log(
          "[Racking Inspection] Loading questions - isCompletedInspection:",
          isCompletedInspection
        );
        
        const questions = await fetchRackingInspectionQuestions({
          isCompletedInspection,
          recordId: !isCompletedInspection ? walkRecordId : undefined,
        });
        dispatch(setQuestions(questions));
      } catch (err: any) {
        dispatch(setError(err?.message || "Failed to load questions"));
      }
    };
    loadQuestions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Map questions to sections when loaded
  useEffect(() => {
    if (rackingInspectionQuestions && rackingInspectionQuestions.length > 0) {
      // Regulatory Standards (RS)
      setRegulatoryStandards(
        rackingInspectionQuestions
          .filter((q: any) => q.Section === "RS")
          .map((q: any) => {
            let comment = "";
            let instances = "";
            let img1 = "";
            let img2 = "";
            let desc1 = "";
            let desc2 = "";
            if (walkData && Array.isArray(walkData.RS)) {
              const match = walkData.RS.find(
                (rs: any) =>
                  String(rs.QNUM) === String(q.QNUM)
              );
              if (match) {
                comment = match.COMMENTS ?? match.comments ?? "";
                instances = match.INSTANCES ?? match.instances ?? "";
                img1 = match.IMG1 ?? "";
                img2 = match.IMG2 ?? "";
                desc1 = match.DESC1 ?? "";
                desc2 = match.DESC2 ?? "";
              }
            }
            return {
              id: String(q.ROWKEY),
              description: q.QText,
              comments: comment,
              instances: instances,
              shortForm: q.SHORT_FORM || "",
              QNUM: q.QNUM,
              imageUrl: img1,
              imageUrl2: img2,
              DESC1: desc1,
              DESC2: desc2,
            };
          })
      );
      // Load Capacity Standards (LC)
      setLoadCapacityStandards(
        rackingInspectionQuestions
          .filter((q: any) => q.Section === "LC")
          .map((q: any) => {
            let comment = "";
            let instances = "";
            let img1 = "";
            let img2 = "";
            let desc1 = "";
            let desc2 = "";
            if (walkData && Array.isArray(walkData.LC)) {
              const match = walkData.LC.find(
                (lc: any) =>
                  String(lc.QNUM) === String(q.QNUM)
              );
              if (match) {
                comment = match.COMMENTS ?? match.comments ?? "";
                instances = match.INSTANCES ?? match.instances ?? "";
                img1 = match.IMG1 ?? "";
                img2 = match.IMG2 ?? "";
                desc1 = match.DESC1 ?? "";
                desc2 = match.DESC2 ?? "";
              }
            }
            return {
              id: String(q.ROWKEY),
              description: q.QText,
              comments: comment,
              instances: instances,
              shortForm: q.SHORT_FORM || "",
              QNUM: q.QNUM,
              imageUrl: img1,
              imageUrl2: img2,
              DESC1: desc1,
              DESC2: desc2,
            };
          })
      );
      // Damage Form Questions (DM)
      setDamageQuestions(
        rackingInspectionQuestions.filter((q: any) => q.Section === "DM")
      );
    }
  }, [rackingInspectionQuestions, walkData]);

  // Synchronization useEffect to ensure selectedRS and selectedLC are properly updated 
  // when regulatoryStandards and loadCapacityStandards have been populated from walkData
  useEffect(() => {
    // Only run this if we have walkData and the standards arrays are populated
    if (!walkData || regulatoryStandards.length === 0 || loadCapacityStandards.length === 0) {
      return;
    }

    // Synchronize selectedRS with current regulatoryStandards data if walkData.RS exists
    if (Array.isArray(walkData.RS) && walkData.RS.length > 0) {
      setSelectedRS((prevSelectedRS) => {
        // Check if selectedRS is already populated and has the correct items
        const shouldUpdate = prevSelectedRS.length === 0 || 
          !prevSelectedRS.every(rs => 
            regulatoryStandards.some(std => String(std.QNUM) === String(rs.QNUM))
          );

        if (shouldUpdate) {
          return walkData.RS.map((rsItem: any) => {
            // Find the corresponding item in regulatoryStandards to get the latest data
            const correspondingStandard = regulatoryStandards.find(
              (std) => String(std.QNUM) === String(rsItem.QNUM)
            );
            
            return {
              Status: rsItem.Status ?? 1,
              QNUM: rsItem.QNUM,
              COMMENTS: rsItem.COMMENTS || correspondingStandard?.comments || "",
              INSTANCES: rsItem.INSTANCES || Number(correspondingStandard?.instances) || 0,
              IMG1: correspondingStandard?.imageUrl || rsItem.IMG1 || "",
              IMG2: correspondingStandard?.imageUrl2 || rsItem.IMG2 || "",
              DESC1: correspondingStandard?.description1 || rsItem.DESC1 || correspondingStandard?.description || "",
              DESC2: correspondingStandard?.description2 || rsItem.DESC2 || "",
              imageUrl: correspondingStandard?.imageUrl || rsItem.IMG1 || "",
              imageFileName: correspondingStandard?.imageFileName || rsItem.imageFileName || "",
              imageUrl2: correspondingStandard?.imageUrl2 || rsItem.IMG2 || "",
              imageFileName2: correspondingStandard?.imageFileName2 || rsItem.imageFileName2 || "",
              description1: correspondingStandard?.description1 || rsItem.DESC1 || correspondingStandard?.description || "",
              description2: correspondingStandard?.description2 || rsItem.DESC2 || "",
            };
          });
        }
        return prevSelectedRS;
      });
    }

    // Synchronize selectedLC with current loadCapacityStandards data if walkData.LC exists
    if (Array.isArray(walkData.LC) && walkData.LC.length > 0) {
      setSelectedLC((prevSelectedLC) => {
        // Check if selectedLC is already populated and has the correct items
        const shouldUpdate = prevSelectedLC.length === 0 || 
          !prevSelectedLC.every(lc => 
            loadCapacityStandards.some(std => String(std.QNUM) === String(lc.QNUM))
          );

        if (shouldUpdate) {
          return walkData.LC.map((lcItem: any) => {
            // Find the corresponding item in loadCapacityStandards to get the latest data
            const correspondingStandard = loadCapacityStandards.find(
              (std) => String(std.QNUM) === String(lcItem.QNUM)
            );
            
            return {
              Status: lcItem.Status ?? 1,
              QNUM: lcItem.QNUM,
              COMMENTS: lcItem.COMMENTS || correspondingStandard?.comments || "",
              INSTANCES: lcItem.INSTANCES || Number(correspondingStandard?.instances) || 0,
              IMG1: correspondingStandard?.imageUrl || lcItem.IMG1 || "",
              IMG2: correspondingStandard?.imageUrl2 || lcItem.IMG2 || "",
              DESC1: correspondingStandard?.description1 || lcItem.DESC1 || correspondingStandard?.description || "",
              DESC2: correspondingStandard?.description2 || lcItem.DESC2 || "",
              imageUrl: correspondingStandard?.imageUrl || lcItem.IMG1 || "",
              imageFileName: correspondingStandard?.imageFileName || lcItem.imageFileName || "",
              imageUrl2: correspondingStandard?.imageUrl2 || lcItem.IMG2 || "",
              imageFileName2: correspondingStandard?.imageFileName2 || lcItem.imageFileName2 || "",
              description1: correspondingStandard?.description1 || lcItem.DESC1 || correspondingStandard?.description || "",
              description2: correspondingStandard?.description2 || lcItem.DESC2 || "",
            };
          });
        }
        return prevSelectedLC;
      });
    }
  }, [walkData, regulatoryStandards, loadCapacityStandards]);

  // Signature state (local)
  const [signature, setSignature] = useState<{
    fileName: string;
    imageUrl?: string;
  } | null>(null);
  const [showDamageText, setShowDamageText] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [, setIsPdfExporting] = useState<boolean>(false);
  const [inspectionStatus, setInspectionStatus] = useState<
    "new" | "in-progress" | "completed"
  >("new");
  // Variable to determine if the form should be read-only based on status
  const isReadOnly = inspectionStatus === "completed";
  console.log(
    "RackingInspectionBeginPage - inspectionStatus:",
    inspectionStatus,
    "isReadOnly:",
    isReadOnly
  );
  // Error state for missing instances
  const [missingInstancesError, setMissingInstancesError] = useState(false);
  const [missingLCInstancesError, setMissingLCInstancesError] = useState(false);

  // Scroll to top button state
  const scrollViewRef = useRef<ScrollView>(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollButtonOpacity = useRef(new Animated.Value(0)).current;

  // Responsive sizing for Add Damage button
  const windowWidth = Dimensions.get("window").width;
  const isWeb = Platform.OS === "web";
  const isiPad = Platform.OS === "ios" && windowWidth >= 768;
  const isiPhone = Platform.OS === "ios" && windowWidth < 768;

  // Disable signature if walkData.SIGN.Status is 1
  const signatureDisabled =
    walkData && walkData.SIGN && walkData.SIGN.Status === 1;

  // Clear error as soon as all selected RS have valid INSTANCES
  useEffect(() => {
    if (missingInstancesError) {
      const missing = selectedRS.filter(
        (rs) => !rs.INSTANCES || Number(rs.INSTANCES) === 0
      );
      if (missing.length === 0) {
        setMissingInstancesError(false);
      }
    }
  }, [selectedRS, missingInstancesError]);

  // Clear error as soon as all selected LC have valid INSTANCES
  useEffect(() => {
    if (missingLCInstancesError) {
      const missing = selectedLC.filter(
        (lc) => !lc.INSTANCES || Number(lc.INSTANCES) === 0
      );
      if (missing.length === 0) {
        setMissingLCInstancesError(false);
      }
    }
  }, [selectedLC, missingLCInstancesError]);
  // Holds all user-added damage entries (across multiple NewDamageForm submissions)
  const [allUserDamageData, setAllUserDamageData] = useState<any[]>([]);

  // Always fetch only active items from API, like DamageChecklistTab
  useEffect(() => {
    if (selectedLocation) {
      console.log(
        "RackingInspectionBeginPage - Fetching damage checklist data for location:",
        selectedLocation
      );
      // Only set the filter in Redux - don't need to call setFilters here as we're passing the filter directly
      fetchDamageChecklistData({
        setIsLoading: () => {}, // We could track this separately if needed
        setApiError: () => {}, // We could track this separately if needed
        dispatch,
        location: selectedLocation.name || selectedLocation.displayText,
        filters: { status: "Active" },
        applyFilters: true,
      }).catch((error) => {
        console.error(
          "RackingInspectionBeginPage - Error fetching damage checklist data:",
          error
        );
      });
    }
  }, [dispatch, selectedLocation]);

  const filteredActiveDamageItems = useSelector(
    (state: RootState) =>
      (state as any).rackingDamageChecklist?.damageItems || []
  );

  // Get loading state for damage checklist
  const damageChecklistLoading = useSelector(
    (state: RootState) =>
      (state as any).rackingDamageChecklist?.loading || false
  );

  // Get miscellaneous observations data from Redux (for the new component)
  const miscObservationImages = useSelector(
    (state: RootState) => state.pulseCheck.observationImages || []
  );

  const miscImageDescriptions = useSelector(
    (state: RootState) =>
      state.pulseCheck.miscImageDescription || {
        description1: "",
        description2: "",
      }
  );

  // Track initial state for unsaved changes detection
  const [initialState, setInitialState] = useState<{
    regulatoryStandards: RegulatoryStandardItem[];
    loadCapacityStandards: RegulatoryStandardItem[];
    selectedRS: any[];
    selectedLC: any[];
    signature: { fileName: string; imageUrl?: string } | null;
    allUserDamageData: any[];
    miscObservationImages: any[];
    miscImageDescriptions: { description1: string; description2: string };
  } | null>(null);

  // Function to check if there are unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!initialState) return false;

    // Compare current state with initial state
    const currentState = {
      regulatoryStandards,
      loadCapacityStandards,
      selectedRS,
      selectedLC,
      signature,
      allUserDamageData,
      miscObservationImages,
      miscImageDescriptions,
    };

    // Check each field for changes
    const hasChanges = 
      JSON.stringify(currentState.regulatoryStandards) !== JSON.stringify(initialState.regulatoryStandards) ||
      JSON.stringify(currentState.loadCapacityStandards) !== JSON.stringify(initialState.loadCapacityStandards) ||
      JSON.stringify(currentState.selectedRS) !== JSON.stringify(initialState.selectedRS) ||
      JSON.stringify(currentState.selectedLC) !== JSON.stringify(initialState.selectedLC) ||
      JSON.stringify(currentState.signature) !== JSON.stringify(initialState.signature) ||
      JSON.stringify(currentState.allUserDamageData) !== JSON.stringify(initialState.allUserDamageData) ||
      JSON.stringify(currentState.miscObservationImages) !== JSON.stringify(initialState.miscObservationImages) ||
      JSON.stringify(currentState.miscImageDescriptions) !== JSON.stringify(initialState.miscImageDescriptions);

    console.log("[DEBUG] hasUnsavedChanges:", hasChanges);
    return hasChanges;
  };

  // Set initial state when component is fully loaded
  useEffect(() => {
    if (initialState === null && !questionsLoading) {
      setInitialState({
        regulatoryStandards,
        loadCapacityStandards,
        selectedRS,
        selectedLC,
        signature,
        allUserDamageData,
        miscObservationImages,
        miscImageDescriptions,
      });
    }
  }, [questionsLoading, regulatoryStandards, loadCapacityStandards, selectedRS, selectedLC, signature, allUserDamageData, miscObservationImages, miscImageDescriptions, initialState]);

  // Debug logging for damage items
  useEffect(() => {
    console.log(
      "RackingInspectionBeginPage - filteredActiveDamageItems length:",
      filteredActiveDamageItems.length
    );
    console.log(
      "RackingInspectionBeginPage - filteredActiveDamageItems:",
      filteredActiveDamageItems
    );
    console.log(
      "RackingInspectionBeginPage - allUserDamageData length:",
      allUserDamageData.length
    );
    console.log(
      "RackingInspectionBeginPage - allUserDamageData:",
      allUserDamageData
    );

    // Log the merged data as well
    const mergedData = mergeDamageData(
      filteredActiveDamageItems,
      allUserDamageData
    );
    console.log(
      "RackingInspectionBeginPage - mergedData length:",
      mergedData.length
    );
    console.log("RackingInspectionBeginPage - mergedData:", mergedData);
  }, [filteredActiveDamageItems, allUserDamageData]);
  // Image upload related state
  const [imageUploadModalVisible, setImageUploadModalVisible] =
    useState<boolean>(false);
  const [selectedStandardItem, setSelectedStandardItem] =
    useState<RegulatoryStandardItem | null>(null);
  const [standardType, setStandardType] = useState<
    "regulatory" | "loadCapacity"
  >("regulatory");
  /**
   * Handle field change in regulatory standards
   * @param id Standard ID
   * @param field Field to change
   * @param value New value
   * @param location Location data from Redux
   */
  // Handle field change in regulatory standards and sync to selectedRS
  const handleRegulatoryStandardChange = (
    id: string,
    field: string,
    value: any,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    location?: any
  ) => {
    // If numeric field, only allow numbers
    if (field === "instances" && typeof value === "string") {
      if (!(value === "" || /^\d+$/.test(value))) {
        return; // Don't update if not a valid number
      }
    }
    setRegulatoryStandards((prevStandards) =>
      prevStandards.map((standard) =>
        standard.id === id ? { ...standard, [field]: value } : standard
      )
    );
    // Auto-select the standard if user enters instances without selecting it first
    if (field === "instances" && value !== "" && Number(value) > 0) {
      const standardItem = regulatoryStandards.find(
        (s) => String(s.id) === String(id)
      );
      if (standardItem) {
        const alreadySelected = selectedRS.some(
          (rs) => String(rs.QNUM) === String(standardItem.QNUM)
        );
        if (!alreadySelected) {
          setSelectedRS((prev) => [
            ...prev,
            {
              Status: 1,
              QNUM: standardItem.QNUM,
              COMMENTS: standardItem.comments || "",
              INSTANCES: Number(value) || 0,
              IMG1: standardItem.imageUrl || "",
              IMG2: standardItem.imageUrl2 || "",
              DESC1: standardItem.description1 || standardItem.description || "",
              DESC2: standardItem.description2 || "",
              imageUrl: standardItem.imageUrl || "",
              imageFileName: standardItem.imageFileName || "",
              imageUrl2: standardItem.imageUrl2 || "",
              imageFileName2: standardItem.imageFileName2 || "",
              description1: standardItem.description1 || standardItem.description || "",
              description2: standardItem.description2 || "",
            },
          ]);
        }
      }
    }
    // Always update selectedRS if this item is selected, syncing all relevant fields
    setSelectedRS((prev) =>
      prev.map((rs) => {
        // Find the standard item by id to get its QNUM for correct matching
        const standardItem = regulatoryStandards.find(
          (s) => String(s.id) === String(id)
        );
        const itemQNUM = standardItem?.QNUM;
        if (itemQNUM && String(rs.QNUM) === String(itemQNUM)) {
          // Always sync all fields from regulatoryStandards for this id
          const updatedStandard = regulatoryStandards.find(
            (s) => String(s.id) === String(id)
          );
          let updatedFields: Record<string, any> = {};
          if (updatedStandard) {
            // Sync all fields, but override the current field with the new value
            Object.keys(updatedStandard).forEach((key) => {
              if (key === field) {
                // Always store as number if not empty, else empty string
                const val =
                  field === "instances"
                    ? value === ""
                      ? ""
                      : Number(value)
                    : value;
                updatedFields[key.toUpperCase()] = val;
                updatedFields[key] = val;
              } else {
                // If this is INSTANCES or instances, always store as number if not empty
                if (
                  (key === "INSTANCES" || key === "instances") &&
                  updatedStandard[key] !== ""
                ) {
                  updatedFields[key.toUpperCase()] = Number(
                    updatedStandard[key]
                  );
                  updatedFields[key] = Number(updatedStandard[key]);
                } else {
                  updatedFields[key.toUpperCase()] = updatedStandard[key];
                  updatedFields[key] = updatedStandard[key];
                }
              }
            });
          } else {
            // Fallback: just update the field
            if (field === "instances") {
              updatedFields = {
                INSTANCES: value === "" ? "" : Number(value),
                instances: value === "" ? "" : Number(value),
              };
            } else {
              updatedFields = { [field.toUpperCase()]: value };
            }
          }
          return { ...rs, ...updatedFields };
        }
        return rs;
      })
    );
    // (Error clearing now handled by useEffect on selectedRS)
  };

  // Toggle selection for RS card
  const handleRegulatoryStandardSelect = (
    item: RegulatoryStandardItem,
    selected: boolean
  ) => {
    setSelectedRS((prev) => {
      if (selected) {
        // Add if not already present
        if (prev.some((rs) => String(rs.QNUM) === String(item.QNUM))) {
          return prev;
        }
        return [
          ...prev,
          {
            Status: 1,
            QNUM: item.QNUM,
            COMMENTS: item.comments || "",
            INSTANCES: Number(item.instances) || 0,
            IMG1: item.imageUrl || "",
            IMG2: item.imageUrl2 || "",
            DESC1: item.description1 || item.description || "",
            DESC2: item.description2 || "",
            imageUrl: item.imageUrl || "",
            imageFileName: item.imageFileName || "",
            imageUrl2: item.imageUrl2 || "",
            imageFileName2: item.imageFileName2 || "",
            description1: item.description1 || item.description || "",
            description2: item.description2 || "",
          },
        ];
      } else {
        // Remove by QNUM
        return prev.filter((rs) => String(rs.QNUM) !== String(item.QNUM));
      }
    });
  };
  /**
   * Handle field change in load capacity standards
   * @param id Standard ID
   * @param field Field to change
   * @param value New value
   * @param location Location data from Redux
   */
  // Handle field change in load capacity standards and sync to selectedLC
  const handleLoadCapacityStandardChange = (
    id: string,
    field: string,
    value: any,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    location?: any
  ) => {
    if (field === "instances" && typeof value === "string") {
      if (!(value === "" || /^\d+$/.test(value))) {
        return;
      }
    }
    setLoadCapacityStandards((prevStandards) =>
      prevStandards.map((standard) =>
        standard.id === id ? { ...standard, [field]: value } : standard
      )
    );
    // Auto-select the standard if user enters instances without selecting it first
    if (field === "instances" && value !== "" && Number(value) > 0) {
      const standardItem = loadCapacityStandards.find(
        (s) => String(s.id) === String(id)
      );
      if (standardItem) {
        const alreadySelected = selectedLC.some(
          (lc) => String(lc.QNUM) === String(standardItem.QNUM)
        );
        if (!alreadySelected) {
          setSelectedLC((prev) => [
            ...prev,
            {
              Status: 1,
              QNUM: standardItem.QNUM,
              COMMENTS: standardItem.comments || "",
              INSTANCES: Number(value) || 0,
              IMG1: standardItem.imageUrl || "",
              IMG2: standardItem.imageUrl2 || "",
              DESC1: standardItem.description1 || standardItem.description || "",
              DESC2: standardItem.description2 || "",
              imageUrl: standardItem.imageUrl || "",
              imageFileName: standardItem.imageFileName || "",
              imageUrl2: standardItem.imageUrl2 || "",
              imageFileName2: standardItem.imageFileName2 || "",
              description1: standardItem.description1 || standardItem.description || "",
              description2: standardItem.description2 || "",
            },
          ]);
        }
      }
    }
    // Enhanced sync to maintain consistency - similar to regulatory standards
    setSelectedLC((prev) =>
      prev.map((lc) => {
        // Find the standard item by id to get its QNUM for correct matching
        const standardItem = loadCapacityStandards.find(
          (s) => String(s.id) === String(id)
        );
        const itemQNUM = standardItem?.QNUM;
        if (itemQNUM && String(lc.QNUM) === String(itemQNUM)) {
          // Always sync all fields from loadCapacityStandards for this id
          const updatedStandard = loadCapacityStandards.find(
            (s) => String(s.id) === String(id)
          );
          let updatedFields: Record<string, any> = {};
          if (updatedStandard) {
            // Sync all fields, but override the current field with the new value
            Object.keys(updatedStandard).forEach((key) => {
              if (key === field) {
                // Always store as number if not empty, else empty string
                const val =
                  field === "instances"
                    ? value === ""
                      ? ""
                      : Number(value)
                    : value;
                updatedFields[key.toUpperCase()] = val;
                updatedFields[key] = val;
              } else {
                // If this is INSTANCES or instances, always store as number if not empty
                if (
                  (key === "INSTANCES" || key === "instances") &&
                  updatedStandard[key] !== ""
                ) {
                  updatedFields[key.toUpperCase()] = Number(
                    updatedStandard[key]
                  );
                  updatedFields[key] = Number(updatedStandard[key]);
                } else {
                  updatedFields[key.toUpperCase()] = updatedStandard[key];
                  updatedFields[key] = updatedStandard[key];
                }
              }
            });
          } else {
            // Fallback: just update the field
            if (field === "instances") {
              updatedFields = {
                INSTANCES: value === "" ? "" : Number(value),
                instances: value === "" ? "" : Number(value),
              };
            } else {
              updatedFields = { [field.toUpperCase()]: value };
            }
          }
          return { ...lc, ...updatedFields };
        }
        return lc;
      })
    );
  };

  // Toggle selection for LC card
  const handleLoadCapacityStandardSelect = (
    item: RegulatoryStandardItem,
    selected: boolean
  ) => {
    setSelectedLC((prev) => {
      if (selected) {
        if (prev.some((lc) => String(lc.QNUM) === String(item.QNUM))) {
          return prev;
        }
        return [
          ...prev,
          {
            Status: 1,
            QNUM: item.QNUM,
            COMMENTS: item.comments || "",
            INSTANCES: Number(item.instances) || 0,
            IMG1: item.imageUrl || "",
            IMG2: item.imageUrl2 || "",
            DESC1: item.description1 || item.description || "",
            DESC2: item.description2 || "",
            imageUrl: item.imageUrl || "",
            imageFileName: item.imageFileName || "",
            imageUrl2: item.imageUrl2 || "",
            imageFileName2: item.imageFileName2 || "",
            description1: item.description1 || item.description || "",
            description2: item.description2 || "",
          },
        ];
      } else {
        return prev.filter((lc) => String(lc.QNUM) !== String(item.QNUM));
      }
    });
  };
  // (DEPRECATED) Handle miscellaneous observations change
  // const handleMiscObservationsChange = (text: string) => {
  //   setMiscObservations(text);
  // };

  /**
   * Handle damage form data change
   * @param data Updated damage form data (per-row keyed object)
   */
  // Accepts backend array from NewDamageForm and prepends to allUserDamageData
  const handleDamageFormChange = (data: any[]) => {
    // Ensure both QNUM and SHORT_FORM are set for each entry based on the matching question in damageQuestions
    const dataWithQnumAndShortForm = data.map((entry) => {
      let q = null;

      if (!q && entry.id) {
        q = damageQuestions.find(
          (q) =>
            String(q.ROWKEY) === String(entry.id) ||
            String(q.QNUM) === String(entry.id)
        );
      }
      // Fallback: try to match by QText if needed
      if (!q && entry.QNUM) {
        q = damageQuestions.find(
          (q) => String(q.ROWKEY) === String(entry.QNUM)
        );
      }
      console.log("Matching question for entry:", entry, "Found:", q);
      return {
        ...entry,
        QNUM:
          q && typeof q.QNUM !== "undefined"
            ? q.QNUM
            : q && q.ROWKEY
            ? q.ROWKEY
            : entry.QNUM,
        SHORT_FORM: q && q.SHORT_FORM ? q.SHORT_FORM : entry.SHORT_FORM || "",
      };
    });
    // Prepend new damages to the beginning of the array so they appear at the top
    setAllUserDamageData((prev) => [...dataWithQnumAndShortForm, ...prev]);
    setShowDamageText(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRowClick = () => {
    console.log("row clicked");
  };

  /**
   * Handle image upload for regulatory standard items
   * @param item The standard item to upload an image for
   */
  const handleImageUpload = (item: RegulatoryStandardItem) => {
    // Store the selected item
    setSelectedStandardItem(item);

    // Set the type of standard (regulatory or loadCapacity)
    if (regulatoryStandards.some((s) => s.id === item.id)) {
      setStandardType("regulatory");
    } else if (loadCapacityStandards.some((s) => s.id === item.id)) {
      setStandardType("loadCapacity");
    }

    // Show the image upload modal
    setImageUploadModalVisible(true);
  };

  /**
   * Handle image view for regulatory standard items in completed inspections
   * @param item The standard item to view images for
   */
  const handleImageView = (item: RegulatoryStandardItem) => {
    // Store the selected item
    setSelectedStandardItem(item);

    // Set the type of standard (regulatory or loadCapacity)
    if (regulatoryStandards.some((s) => s.id === item.id)) {
      setStandardType("regulatory");
    } else if (loadCapacityStandards.some((s) => s.id === item.id)) {
      setStandardType("loadCapacity");
    }

    // Show the image upload modal in view-only mode
    setImageUploadModalVisible(true);
  };

  /**
   * Handle successful image upload
   * @param item The standard item
   * @param imageUrl URL to the uploaded image
   * @param fileName Filename of the uploaded image
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleImageUploaded = (
    item: RegulatoryStandardItem,
    imageUrl: string,
    fileName: string
  ) => {
    // Update the standard item with the image URL and filename
    if (standardType === "regulatory") {
      setRegulatoryStandards((prevStandards) =>
        prevStandards.map((standard) =>
          standard.id === item.id
            ? { ...standard, imageUrl, imageFileName: fileName }
            : standard
        )
      );
      // Also update in selectedRS if present
      setSelectedRS((prev) =>
        prev.map((rs) =>
          String(rs.QNUM) === String(item.QNUM)
            ? { ...rs, IMG1: imageUrl, imageUrl, imageFileName: fileName }
            : rs
        )
      );
    } else {
      setLoadCapacityStandards((prevStandards) =>
        prevStandards.map((standard) =>
          standard.id === item.id
            ? { ...standard, imageUrl, imageFileName: fileName }
            : standard
        )
      );
      setSelectedLC((prev) =>
        prev.map((lc) =>
          String(lc.QNUM) === String(item.QNUM)
            ? { ...lc, IMG1: imageUrl, imageUrl, imageFileName: fileName }
            : lc
        )
      );
    }
    // Show success toast notification
    toast.showToast({
      header: "Success",
      subtext: "Image uploaded successfully",
      variant: "success",
    });
  };

  // Helper to show toast
  const showToast = (
    header: string,
    subtext: string,
    variant: "success" | "error" | "warning"
  ) => {
    toast.showToast({ header, subtext, variant });
  };

  // Helper to submit inspection to backend and update status
  const handleInspectionAction = async (
    status: "in-progress" | "completed"
  ) => {
    // Validation: No. of Instances must be filled for each selected regulatory standard
    // Use the latest values from regulatoryStandards for the selected QNUMs
    const selectedQnums = selectedRS.map((rs) => String(rs.QNUM));
    const missingInstances = regulatoryStandards.filter(
      (s) =>
        selectedQnums.includes(String(s.QNUM)) &&
        (s.instances === "" ||
          isNaN(Number(s.instances)) ||
          Number(s.instances) <= 0)
    );
    if (missingInstances.length > 0) {
      setMissingInstancesError(true);
      showToast(
        "Error",
        "Please enter a valid (greater than 0) 'No. of Instances' for all selected Regulatory Standards.",
        "error"
      );
      return false;
    }
    // Validation: No. of Instances must be filled for each selected load capacity standard
    const selectedLCQnums = selectedLC.map((lc) => String(lc.QNUM));
    const missingLCInstances = loadCapacityStandards.filter(
      (s) =>
        selectedLCQnums.includes(String(s.QNUM)) &&
        (s.instances === "" ||
          isNaN(Number(s.instances)) ||
          Number(s.instances) <= 0)
    );
    if (missingLCInstances.length > 0) {
      setMissingLCInstancesError(true);
      showToast(
        "Error",
        "Please enter a valid (greater than 0) 'No. of Instances' for all selected Load Capacity Standards.",
        "error"
      );
      return false;
    }
    if (!selectedLocation) {
      showToast(
        "Error",
        `Please select a location before ${
          status === "completed" ? "completing" : "saving"
        }`,
        "error"
      );
      return false;
    }
    setIsSubmitting(true);
    try {
      console.log(
        `RackingInspectionBeginPage - handleInspectionAction called with status: ${status}`
      );
      console.log(
        "[DEBUG] Regulatory Standards before save:",
        regulatoryStandards
      );
      console.log(
        "[DEBUG] Load Capacity Standards before save:",
        loadCapacityStandards
      );
      // Construct payload as per required structure
      const locationName =
        selectedLocation.name || selectedLocation.displayText || "";
      const userEmailStr = userEmail || "";
      const periodWeekStr = periodWeek || "";
      // Helper to map signature
      const signaturePayload = signature
        ? {
            Status: status === "completed" ? 1 : 0,
            Signature: signature.imageUrl || "",
          }
        : { Status: 0, Signature: "" };

      // Helper to map miscState from Redux data
      const miscPayload = {
        Status: status === "completed" ? 1 : 0,
        IMG1:
          miscObservationImages.length > 0
            ? miscObservationImages[0]?.imageUrl || ""
            : "",
        IMG2:
          miscObservationImages.length > 1
            ? miscObservationImages[1]?.imageUrl || ""
            : "",
        DESC1: miscImageDescriptions.description1 || "",
        DESC2: miscImageDescriptions.description2 || "",
      };

      // Build RS and LC arrays from all standards with user input
      const buildStandardArr = (standards: any[]) =>
        standards
          .filter(
            (s) =>
              (s.comments && s.comments.trim() !== "") ||
              (s.instances && String(s.instances).trim() !== "")
          )
          .map((s) => ({
            Status: status === "completed" ? 1 : 0,
            QNUM: Number(s.QNUM) || 0,
            COMMENTS: s.comments || "",
            INSTANCES: s.instances === "" ? 0 : Number(s.instances),
            IMG1: s.imageUrl || "",
            IMG2: s.imageUrl2 || "",
            DESC1: s.description || "",
            DESC2: s.DESC2 || "",
          }));

      // Helper to map DM array
      const mapDamageArr = (arr: any[]) =>
        arr.map((item) => ({
          IS_DOR_COMPLETE: item.IS_DOR_COMPLETE ?? false,
          AISLE: Number(item.AISLE ?? item.aisle ?? 0),
          BAY_NUM: Number(item.BAY_NUM ?? item.bay ?? 0),
          LEVEL: Number(item.LEVEL ?? item.level ?? 0),
          SLOT_ID: Number(item.SLOT_ID ?? item.slotId ?? 0),
          Signature: item.Signature || "",
          DORCOMPLETION: item.DORCOMPLETION || "",
          Status: 0, // Always 0 for DM (damage)
          QNUM: Number(item.QNUM) || 0,
          COMMENTS: item.COMMENTS || "",
          INSTANCES: Number(item.INSTANCES) || 0,
          SEVERITY: item.SEVERITY || "Low",
          DOR: item.DOR || "",
          IMG1: item.IMG1 || "",
          IMG2: item.IMG2 || "",
          DESC1: item.DESC1 || "",
          DESC2: item.DESC2 || "",
        }));

      // Always set oneSubmission/selectedRecord if walkData.Status === 0 and walkData.uid exists
      let _oneSubmission = oneSubmission;
      let _selectedRecord = selectedRecord;
      if (walkData && Number(walkData.Status) === 0 && walkData.uid) {
        _oneSubmission = true;
        _selectedRecord = { id: walkData.uid };
      }
      console.log(
        "RackingInspectionBeginPage - handleInspectionAction - oneSubmission:",
        _oneSubmission,
        "selectedRecord:",
        _selectedRecord
      );
      const payload = {
        oneSubmission: _oneSubmission,
        selectedRecord: _selectedRecord,
        LOC: locationName,
        PW: periodWeekStr,
        WALK_STARTED_BY: walkData ? walkData.WALK_STARTED_BY : userEmailStr,
        LAST_UPDT_BY: userEmailStr,
        LAST_UPDT_BY_USERNAME: userEmailStr,
        SIGN: signaturePayload,
        MSC: miscPayload,
        LC: buildStandardArr(loadCapacityStandards),
        RS: buildStandardArr(regulatoryStandards),
        DM: mapDamageArr(allUserDamageData),
      };
      console.log(
        "[DEBUG] Payload being sent to backend:",
        JSON.stringify(payload, null, 2)
      );
      console.log(
        `${status === "completed" ? "Completing" : "Saving"} inspection:`,
        payload
      );

      // Call the real backend API
      await submitRackingInspection(payload);

      setInspectionStatus(status);

      // Call onSave with the status - success message will be handled in parent component
      onSave(status);

      // Reset initial state since changes have been saved
      setInitialState({
        regulatoryStandards,
        loadCapacityStandards,
        selectedRS,
        selectedLC,
        signature,
        allUserDamageData,
        miscObservationImages,
        miscImageDescriptions,
      });

      return true;
    } catch (error) {
      console.error(
        `Error ${status === "completed" ? "completing" : "saving"} inspection:`,
        error
      );
      showToast(
        "Error",
        `Failed to ${
          status === "completed" ? "complete" : "save"
        } Racking Inspection`,
        "error"
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Save for Later handler
  const handleSaveForLater = () => handleInspectionAction("in-progress");
  // Complete handler
  const handleComplete = () => handleInspectionAction("completed");

  // Scroll handling functions
  const handleScroll = (event: any) => {
    const { contentOffset } = event.nativeEvent;
    const shouldShow = contentOffset.y > 200;

    if (shouldShow !== showScrollToTop) {
      setShowScrollToTop(shouldShow);
      Animated.timing(scrollButtonOpacity, {
        toValue: shouldShow ? 1 : 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

  const scrollToTop = () => {
    try {
      if (Platform.OS === "web") {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      } else if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          x: 0,
          y: 0,
          animated: true,
        });
      }
    } catch (scrollError) {
      console.error("Error scrolling to top:", scrollError);
    }
  };

  // Handle scroll events for web
  useEffect(() => {
    if (Platform.OS === "web") {
      const handleWebScroll = () => {
        const shouldShow = window.scrollY > 200;

        if (shouldShow !== showScrollToTop) {
          setShowScrollToTop(shouldShow);

          Animated.timing(scrollButtonOpacity, {
            toValue: shouldShow ? 1 : 0,
            duration: 300,
            useNativeDriver: true,
          }).start();
        }
      };

      window.addEventListener("scroll", handleWebScroll);
      return () => window.removeEventListener("scroll", handleWebScroll);
    }
  }, [showScrollToTop, scrollButtonOpacity]);
  /**
   * Handle PDF export
   * Generates and emails a PDF report
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePdfExport = async () => {
    // Don't allow export if inspection is new (hasn't been saved yet)
    if (inspectionStatus === "new") {
      toast.showToast({
        header: "PDF Export Unavailable",
        subtext: "Please save your inspection first before exporting",
        variant: "warning",
      });
      return;
    }

    // Check if user email is available
    if (!userEmail) {
      toast.showToast({
        header: "Email Not Available",
        subtext:
          "User email could not be retrieved. Please check your account settings.",
        variant: "warning",
      });
      return;
    }

    setIsPdfExporting(true);
    try {
      // TODO: Implement API call to generate and send PDF
      // This should be replaced with actual API implementation
      // similar to the observation module
      console.log("Exporting PDF for racking inspection to:", userEmail);

      // Create a subject line for the email
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const subjectLine = `Racking Inspection Report - ${
        periodWeek || "New Inspection"
      }`;

      // Simulate API call
      // The actual implementation should call something like:
      // const api = createApiClient();
      // const response = await apiClient(
      //   api,
      //   `/notifications/riseqa/email/pdf?emailTo=${encodeURIComponent(userEmail)}&inspection_module=racking&id=${inspectionId}&subject=${encodeURIComponent(subjectLine)}`,
      //   "GET"
      // );

      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Show success notification
      toast.showToast({
        header: "PDF Exported",
        subtext: `Racking Inspection report has been sent to ${userEmail}`,
        variant: "success",
      });
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.showToast({
        header: "Export Error",
        subtext: "Failed to generate PDF report",
        variant: "error",
      });
    } finally {
      setIsPdfExporting(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={[{ flex: 1 }, isDark && { backgroundColor: colors.background.page }]}
    >
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={[styles.container, isDark && { backgroundColor: colors.background.page }]}
        onScroll={Platform.OS !== "web" ? handleScroll : undefined}
        scrollEventThrottle={16}
      >
        <View style={styles.innerContainer}>
          {/* Use LHRackingHeader component */}
          <LHRackingHeader
            title="Racking Inspection"
            subtitle={periodWeek || "New Inspection"}
            onReturn={onBackPress}
            onHomePress={onHomePress || onBackPress}
            status={inspectionStatus}
            hasUnsavedChanges={hasUnsavedChanges()}
          />
          {/* Show loading indicator while fetching questions */}
          {questionsLoading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={isDark ? colors.brand.primary : "#0066CC"} />
              <Text style={[styles.loadingText, isDark && { color: colors.brand.primary }]}>
                Loading racking inspection details...
              </Text>
            </View>
          )}

          {/* Regulatory Standards Section */}
          {!questionsLoading && (
            <View style={styles.seperateCardContainers}>
              {/* Card with integrated header and body */}
              <View style={styles.cardContainer}>
                <LHSectionHeader
                  title="Regulatory Standards"
                  containerStyle={styles.sectionHeader}
                  textStyle={styles.sectionHeaderText}
                />

                <View style={[styles.miscellaneousObservationsContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
                  {/* Conditionally render cards for iOS mobile or table for other platforms */}
                  {isiPhone ? (
                    // iOS Mobile Card Layout - Each record as a separate card
                    <View style={styles.cardsListContainer}>
                      {regulatoryStandards.map((item, index) => {
                        const isSelected = selectedRS.some(
                          (rs) => String(rs.QNUM) === String(item.QNUM)
                        );
                        const hasUploadedImage =
                          !!item.imageUrl || !!item.imageUrl2;
                        return (
                          <View key={`regulatory-record-${item.id || index}`} style={[styles.damageCardContainer, isDark && { backgroundColor: colors.background.elevated, shadowColor: "transparent" }]}>
                            {/* Card content with all columns */}
                            <View style={styles.damageCardContent}>
                              
                              {/* Column 1: Standard */}
                              <View style={styles.damageCardRow}>
                                <Text style={[styles.damageCardRowLabel, isDark && { color: colors.text.secondary }]}>Standard:</Text>
                                <View style={{ marginTop: 8 }}>
                                  <LHRackingInspectionOptionCard
                                    title={item.description}
                                    selected={isSelected}
                                    onPress={() =>
                                      isReadOnly
                                        ? null
                                        : handleRegulatoryStandardSelect(
                                            item,
                                            !isSelected
                                          )
                                    }
                                    onCameraPress={() =>
                                      isReadOnly ? null : handleImageUpload(item)
                                    }
                                    onImageView={() => handleImageView(item)}
                                    hasUploadedImage={hasUploadedImage}
                                    disabled={isReadOnly}
                                    isCompleted={isReadOnly}
                                    style={{
                                      padding: 12,
                                      marginVertical: 0,
                                      minHeight: 44,
                                    }}
                                  />
                                </View>
                              </View>

                              {/* Column 2: Comments */}
                              <View style={styles.damageCardRow}>
                                <Text style={[styles.damageCardRowLabel, isDark && { color: colors.text.secondary }]}>Comments:</Text>
                                <View style={{ marginTop: 8 }}>
                                  <TextInput
                                    style={{
                                      borderWidth: 1,
                                      borderColor: isDark ? colors.border.default : "#ced4da",
                                      borderRadius: 8,
                                      backgroundColor: isDark ? colors.input.background : "white",
                                      color: isDark ? colors.text.inverse : undefined,
                                      fontSize: 16,
                                      paddingHorizontal: 12,
                                      paddingVertical: 10,
                                      minHeight: 44,
                                      textAlignVertical: "top",
                                    }}
                                    value={item.comments?.toString() || ""}
                                    onChangeText={(text: string) => handleRegulatoryStandardChange(item.id, "comments", text)}
                                    editable={!isReadOnly}
                                    multiline={true}
                                    placeholder="Enter comments"
                                    placeholderTextColor="#999"
                                  />
                                </View>
                              </View>

                              {/* Column 3: No. Of Instances */}
                              <View style={styles.damageCardRow}>
                                <Text style={[styles.damageCardRowLabel, isDark && { color: colors.text.secondary }]}>No. Of Instances:</Text>
                                <View style={{ marginTop: 8 }}>
                                  <TextInput
                                    style={{
                                      borderWidth: 1,
                                      borderColor: isDark ? colors.border.default : "#ced4da",
                                      borderRadius: 8,
                                      backgroundColor: isDark ? colors.input.background : "white",
                                      color: isDark ? colors.text.inverse : undefined,
                                      fontSize: 16,
                                      paddingHorizontal: 12,
                                      paddingVertical: 10,
                                      minHeight: 44,
                                      textAlign: "center",
                                    }}
                                    value={item.instances?.toString() || ""}
                                    onChangeText={(text: string) => handleRegulatoryStandardChange(item.id, "instances", text)}
                                    editable={!isReadOnly}
                                    keyboardType="numeric"
                                    placeholder="0"
                                    placeholderTextColor="#999"
                                  />
                                </View>
                              </View>

                            </View>
                          </View>
                        );
                      })}
                    </View>
                  ) : (
                    // Desktop/iPad/Android Table Layout
                    <RegulatoryStandards
                      data={regulatoryStandards}
                      editable={!isReadOnly}
                      isCompleted={isReadOnly}
                      columns={[
                        {
                          id: "description",
                          title: "Standard",
                          width: 3,
                          renderCell: (item) => {
                            const isSelected = selectedRS.some(
                              (rs) => String(rs.QNUM) === String(item.QNUM)
                            );
                            // Highlight camera if any image is uploaded
                            const hasUploadedImage =
                              !!item.imageUrl || !!item.imageUrl2;
                            return (
                              <LHRackingInspectionOptionCard
                                title={item.description}
                                selected={isSelected}
                                onPress={() =>
                                  isReadOnly
                                    ? null
                                    : handleRegulatoryStandardSelect(
                                        item,
                                        !isSelected
                                      )
                                }
                                onCameraPress={() =>
                                  isReadOnly ? null : handleImageUpload(item)
                                }
                                onImageView={() => handleImageView(item)}
                                hasUploadedImage={hasUploadedImage}
                                disabled={isReadOnly}
                                isCompleted={isReadOnly}
                                style={{
                                  height:
                                    Platform.OS === "web" ? "80%" : undefined,
                                  padding: "4%",
                                }}
                              />
                            );
                          },
                        },
                        ...createDefaultColumns(undefined, undefined, isReadOnly, isDark && !isWeb, isDark && !isWeb ? colors : undefined).slice(1),
                      ]}
                      onChange={handleRegulatoryStandardChange}
                      onImageView={handleImageView}
                      headerStyle={styles.regulatoryHeader}
                    />
                  )}
                </View>
              </View>
            </View>
          )}

          {/* Load Capacity Standards Section */}
          {!questionsLoading && (
            <View style={styles.seperateCardContainers}>
              {/* Card with integrated header and body */}
              <View style={[styles.cardContainer]}>
                <LHSectionHeader
                  title="Load Capacity Standards"
                  containerStyle={styles.sectionHeader}
                  textStyle={styles.sectionHeaderText}
                />

                <View style={[styles.miscellaneousObservationsContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
                  {/* Conditionally render cards for iOS mobile or table for other platforms */}
                  {isiPhone ? (
                    // iOS Mobile Card Layout - Each record as a separate card
                    <View style={styles.cardsListContainer}>
                      {loadCapacityStandards.map((item, index) => {
                        const isSelected = selectedLC.some(
                          (lc) => String(lc.QNUM) === String(item.QNUM)
                        );
                        const hasUploadedImage =
                          !!item.imageUrl || !!item.imageUrl2;
                        return (
                          <View key={`load-capacity-record-${item.id || index}`} style={[styles.damageCardContainer, isDark && { backgroundColor: colors.background.elevated, shadowColor: "transparent" }]}>
                            {/* Card content with all columns */}
                            <View style={styles.damageCardContent}>
                              
                              {/* Column 1: Standard */}
                              <View style={styles.damageCardRow}>
                                <Text style={[styles.damageCardRowLabel, isDark && { color: colors.text.secondary }]}>Standard:</Text>
                                <View style={{ marginTop: 8 }}>
                                  <LHRackingInspectionOptionCard
                                    title={item.description}
                                    selected={isSelected}
                                    onPress={() =>
                                      isReadOnly
                                        ? null
                                        : handleLoadCapacityStandardSelect(
                                            item,
                                            !isSelected
                                          )
                                    }
                                    onCameraPress={() =>
                                      isReadOnly ? null : handleImageUpload(item)
                                    }
                                    onImageView={() => handleImageView(item)}
                                    hasUploadedImage={hasUploadedImage}
                                    disabled={isReadOnly}
                                    isCompleted={isReadOnly}
                                    style={{
                                      padding: 12,
                                      marginVertical: 0,
                                      minHeight: 44,
                                    }}
                                  />
                                </View>
                              </View>

                              {/* Column 2: Comments */}
                              <View style={styles.damageCardRow}>
                                <Text style={[styles.damageCardRowLabel, isDark && { color: colors.text.secondary }]}>Comments:</Text>
                                <View style={{ marginTop: 8 }}>
                                  <TextInput
                                    style={{
                                      borderWidth: 1,
                                      borderColor: isDark ? colors.border.default : "#ced4da",
                                      borderRadius: 8,
                                      backgroundColor: isDark ? colors.input.background : "white",
                                      color: isDark ? colors.text.inverse : undefined,
                                      fontSize: 16,
                                      paddingHorizontal: 12,
                                      paddingVertical: 10,
                                      minHeight: 44,
                                      textAlignVertical: "top",
                                    }}
                                    value={item.comments?.toString() || ""}
                                    onChangeText={(text: string) => handleLoadCapacityStandardChange(item.id, "comments", text)}
                                    editable={!isReadOnly}
                                    multiline={true}
                                    placeholder="Enter comments"
                                    placeholderTextColor="#999"
                                  />
                                </View>
                              </View>

                              {/* Column 3: No. Of Instances */}
                              <View style={styles.damageCardRow}>
                                <Text style={[styles.damageCardRowLabel, isDark && { color: colors.text.secondary }]}>No. Of Instances:</Text>
                                <View style={{ marginTop: 8 }}>
                                  <TextInput
                                    style={{
                                      borderWidth: 1,
                                      borderColor: isDark ? colors.border.default : "#ced4da",
                                      borderRadius: 8,
                                      backgroundColor: isDark ? colors.input.background : "white",
                                      color: isDark ? colors.text.inverse : undefined,
                                      fontSize: 16,
                                      paddingHorizontal: 12,
                                      paddingVertical: 10,
                                      minHeight: 44,
                                      textAlign: "center",
                                    }}
                                    value={item.instances?.toString() || ""}
                                    onChangeText={(text: string) => handleLoadCapacityStandardChange(item.id, "instances", text)}
                                    editable={!isReadOnly}
                                    keyboardType="numeric"
                                    placeholder="0"
                                    placeholderTextColor="#999"
                                  />
                                </View>
                              </View>

                            </View>
                          </View>
                        );
                      })}
                    </View>
                  ) : (
                    // Desktop/iPad/Android Table Layout
                    <RegulatoryStandards
                      data={loadCapacityStandards}
                      editable={!isReadOnly}
                      isCompleted={isReadOnly}
                      columns={[
                        {
                          id: "description",
                          title: "Standard",
                          width: 3,
                          renderCell: (item) => {
                            const isSelected = selectedLC.some(
                              (lc) => String(lc.QNUM) === String(item.QNUM)
                            );
                            // Highlight camera if any image is uploaded
                            const hasUploadedImage =
                              !!item.imageUrl || !!item.imageUrl2;
                            return (
                              <LHRackingInspectionOptionCard
                                title={item.description}
                                selected={isSelected}
                                onPress={() =>
                                  isReadOnly
                                    ? null
                                    : handleLoadCapacityStandardSelect(
                                        item,
                                        !isSelected
                                      )
                                }
                                onCameraPress={() =>
                                  isReadOnly ? null : handleImageUpload(item)
                                }
                                onImageView={() => handleImageView(item)}
                                hasUploadedImage={hasUploadedImage}
                                disabled={isReadOnly}
                                isCompleted={isReadOnly}
                                style={{
                                  height:
                                    Platform.OS === "web" ? "80%" : undefined,
                                  padding: "4%",
                                }}
                              />
                            );
                          },
                        },
                        ...createDefaultColumns(undefined, undefined, isReadOnly, isDark && !isWeb, isDark && !isWeb ? colors : undefined).slice(1),
                      ]}
                      onChange={handleLoadCapacityStandardChange}
                      onImageView={handleImageView}
                      headerStyle={styles.loadCapacityHeader}
                    />
                  )}
                </View>
              </View>
            </View>
          )}

          {/* Damage Section - show table or NewDamageForm based on state */}
          {!questionsLoading && !showDamageText && (
            <View style={styles.seperateCardContainers}>
              {/* Button placed above the card, aligned to the right */}
              <View style={[styles.damageButtonContainer]}>
                <LhButton
                  variant="primary"
                  text="Add Damage"
                  style={[
                    styles.damageButton,
                    isWeb
                      ? { width: "16%" }
                      : isiPad
                      ? { width: "30%" }
                      : isiPhone
                      ? { width: "40%" }
                      : { width: "30%" },
                  ]}
                  disabled={isReadOnly || isSubmitting}
                  onPress={() => {
                    console.log("Damage button pressed", allUserDamageData);
                    setShowDamageText(!showDamageText);
                  }}
                />
              </View>

              {/* Card with integrated header and body */}
              <View style={styles.cardContainer}>
                <View style={[styles.sectionHeader]}>
                  <View style={{ flex: 1 }}>
                    <LHSectionHeader
                      title="Damage Checklist"
                      containerStyle={[
                        styles.sectionHeader,
                        { backgroundColor: "transparent" },
                      ]}
                      textStyle={styles.sectionHeaderText}
                    />
                  </View>
                </View>

                <View style={[styles.miscellaneousObservationsContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
                  {/* Conditionally render cards for iOS mobile or table for other platforms */}
                  {isiPhone ? (
                    // iOS Mobile Card Layout
                    <View style={styles.damageCardsContainer}>
                      {damageChecklistLoading ? (
                        <View style={styles.loadingContainer}>
                          <ActivityIndicator size="large" color={isDark ? colors.brand.primary : "#0066CC"} />
                          <Text style={[styles.loadingText, isDark && { color: colors.brand.primary }]}>
                            Loading damage checklist...
                          </Text>
                        </View>
                      ) : (
                        <View style={styles.cardsListContainer}>
                          {mergeDamageData(
                            filteredActiveDamageItems,
                            allUserDamageData
                          ).map((item, index) => (
                            <DamageCardItem
                              key={`damage-card-${index}`}
                              item={item}
                              columns={tableColumns}
                            />
                          ))}
                        </View>
                      )}
                    </View>
                  ) : (
                    // Desktop/iPad/Android Table Layout
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={true}
                      style={styles.scrollContainer}
                    >
                      <View style={styles.tableContainer}>
                        {damageChecklistLoading ? (
                          <View style={styles.loadingContainer}>
                            <ActivityIndicator size="large" color="#0066CC" />
                            <Text style={styles.loadingText}>
                              Loading damage checklist...
                            </Text>
                          </View>
                        ) : (
                          <LHDataTable
                            data={mergeDamageData(
                              filteredActiveDamageItems,
                              allUserDamageData
                            )}
                            columns={tableColumns}
                            tableHeader="Damage Records"
                            tableDescription="List of all recorded damage items"
                          />
                        )}
                      </View>
                    </ScrollView>
                  )}
                </View>
              </View>
            </View>
          )}

          {/* Damage Form Section - show questions from DM section */}
          {!questionsLoading && showDamageText && (
            <View style={styles.seperateCardContainers}>
              {/* Card with integrated header and body */}
              <View style={styles.cardContainer}>
                <LHSectionHeader
                  title="Damage Report Form"
                  containerStyle={styles.sectionHeader}
                  textStyle={styles.sectionHeaderText}
                />

                <View style={[styles.miscellaneousObservationsContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
                  <View style={[styles.contentCard, isDark && { backgroundColor: colors.background.surface, shadowColor: "transparent" }]}>
                    <View style={[styles.contentBox, isDark && { backgroundColor: colors.background.surface }]}>
                      {/* Render DM questions as a list */}

                      {/* New DamageForm: table/grid style, two images per row, per requirements */}
                      <NewDamageForm
                        questions={damageQuestions.map((q) => ({
                          id: String(q.ROWKEY),
                          QText: q.QText,
                        }))}
                        onChange={handleDamageFormChange}
                        disabled={isReadOnly}
                      />
                    </View>
                  </View>
                </View>
              </View>
            </View>
          )}

          {/* Miscellaneous Observations */}
          {!questionsLoading && (
            <View style={styles.seperateCardContainers}>
              {/* Card with integrated header and body */}
              <View style={[styles.cardContainer]}>
                <LHSectionHeader
                  title="Miscellaneous Observations"
                  containerStyle={styles.sectionHeader}
                  textStyle={styles.sectionHeaderText}
                />

                <View style={[styles.miscellaneousObservationsContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
                  <LHMiscellaneousObservations
                    disabled={isSubmitting || isReadOnly}
                    stateKey="pulseCheck"
                    containerName="racking"
                  />
                </View>
              </View>
            </View>
          )}

          {/* Signature Module */}
          {!questionsLoading && (
            <View style={styles.seperateCardContainers}>
              <LHSectionHeader
                title="Assessor's Signature"
                textStyle={styles.header1text}
                containerStyle={styles.sectionHeader}
              />
              <View style={styles.contentBox}>
                {/* Single Signature Pad Container */}
                <View style={{ backgroundColor: isDark ? colors.background.surface : "white" }}>
                  <View style={[styles.signaturePadWrapper, isDark && { backgroundColor: colors.background.surface }]}>
                    <View style={styles.signaturePadContainer}>
                      <SignaturePad
                        containerName="racking"
                        isCompleted={isReadOnly}
                        onOK={(fileName: string, imageUrl?: string) => {
                          console.log(
                            "RackingInspection: Received signature data - fileName:",
                            fileName,
                            "imageUrl:",
                            imageUrl
                          );
                          setSignature({
                            fileName,
                            imageUrl,
                          });
                        }}
                        onClear={() => {
                          console.log(
                            "RackingInspection: Clearing signature data"
                          );
                          setSignature(null);
                        }}
                        initialImageUrl={signature?.imageUrl || undefined}
                        disabled={
                          isSubmitting || isReadOnly || signatureDisabled
                        }
                        isDarkTheme={isDark}
                      />
                      {signature && (
                        <Text style={[styles.signatureText, isDark && { color: colors.text.secondary }]}>
                          Assessor Signature uploaded
                        </Text>
                      )}
                    </View>
                  </View>

                  {/* Status Summary */}
                  <View style={[styles.statusContainer, isDark && { backgroundColor: colors.background.surface, borderTopColor: colors.border.default }]}>
                    <View style={{ alignItems: "center" }}>
                      <View style={styles.statusRow}>
                        <Text style={[styles.statusLabel, isDark && { color: colors.text.secondary }]}>Assessor:</Text>
                        <Text
                          style={[
                            styles.statusValue,
                            signature
                              ? styles.statusComplete
                              : styles.statusPending,
                          ]}
                        >
                          {signature ? "Signed" : "Pending"}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          )}
          {/* Action Buttons Section */}
          {!questionsLoading && (
            <RackingInspectionActionButtons
              onSave={handleSaveForLater}
              onComplete={handleComplete}
              inspectionId={selectedRecord?.id || walkData?.uid}
              walkStartedBy={walkData?.WALK_STARTED_BY || selectedRecord?.id}
              location={
                walkData?.LOC ||
                selectedLocation?.name ||
                selectedLocation?.displayText
              }
              status={isReadOnly ? 1 : 0}
              isReadOnly={isReadOnly}
              signatureRequired={true}
              hasSignature={!!signature}
              disableActions={isSubmitting}
            />
          )}

          {/* <Text style={styles.footer}>
            © 2025 PepsiCo | Racking Inspection System v1.0
          </Text> */}
        </View>
      </ScrollView>
      {/* Regulatory Standards Image Upload Modal */}
      {selectedStandardItem && (
        <DamageImageUploadModal
          visible={imageUploadModalVisible}
          onClose={() => setImageUploadModalVisible(false)}
          viewOnly={isReadOnly}
          onImageUploaded={(data) => {
            // Save both images, filenames, and descriptions
            const {
              imageUrl1,
              fileName1,
              description1,
              imageUrl2,
              fileName2,
              description2,
            } = data;
            
            // Create the image update object with proper type conversion
            const imageUpdate = {
              IMG1: imageUrl1 || "",
              imageUrl: imageUrl1 || "",
              imageFileName: fileName1,
              description1: description1,
              IMG2: imageUrl2 || "",
              imageUrl2: imageUrl2 || "",
              imageFileName2: fileName2,
              description2: description2,
            };
            
            if (standardType === "regulatory") {
              // Update regulatory standards array
              setRegulatoryStandards((prevStandards) =>
                prevStandards.map((standard) =>
                  standard.id === selectedStandardItem.id
                    ? {
                        ...standard,
                        ...imageUpdate,
                      }
                    : standard
                )
              );
              
              // Update selected regulatory standards to maintain consistency
              setSelectedRS((prev) =>
                prev.map((rs) =>
                  String(rs.QNUM) === String(selectedStandardItem.QNUM)
                    ? {
                        ...rs,
                        ...imageUpdate,
                      }
                    : rs
                )
              );
            } else {
              // Update load capacity standards array
              setLoadCapacityStandards((prevStandards) =>
                prevStandards.map((standard) =>
                  standard.id === selectedStandardItem.id
                    ? {
                        ...standard,
                        ...imageUpdate,
                      }
                    : standard
                )
              );
              
              // Update selected load capacity standards to maintain consistency
              setSelectedLC((prev) =>
                prev.map((lc) =>
                  String(lc.QNUM) === String(selectedStandardItem.QNUM)
                    ? {
                        ...lc,
                        ...imageUpdate,
                      }
                    : lc
                )
              );
            }
          }}
          initialImageUrl1={selectedStandardItem.imageUrl || ""}
          initialDescription1={
            selectedStandardItem.description1 ||
            selectedStandardItem.description ||
            ""
          }
          initialImageUrl2={selectedStandardItem.imageUrl2 || ""}
          initialDescription2={
            selectedStandardItem.description2 ||
            selectedStandardItem.description ||
            ""
          }
          allowSecondImage={true}
          image1Required={false}
          image2Required={false}
        />
      )}

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <Animated.View
          style={[
            styles.scrollToTopButton,
            {
              opacity: scrollButtonOpacity,
              transform: [
                {
                  scale: scrollButtonOpacity.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.8, 1],
                  }),
                },
              ],
            },
          ]}
          pointerEvents={showScrollToTop ? "auto" : "none"}
        >
          <TouchableOpacity
            style={[styles.scrollToTopTouchable, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}
            onPress={scrollToTop}
            activeOpacity={0.7}
          >
            <View style={styles.arrowIcon}>
              <Text style={[styles.arrowText, isDark && { color: colors.brand.primary }]}>↑</Text>
            </View>
          </TouchableOpacity>
        </Animated.View>
      )}
    </KeyboardAvoidingView>
  );
};

// Card component for iOS mobile damage display
interface DamageCardItemProps {
  item: any;
  columns: any[];
}

const DamageCardItem: React.FC<DamageCardItemProps> = ({ item, columns }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  // Get the status tag style based on status value
  const getStatusTagStyle = (status: any) => {
    const normalized = String(status || "")
      .toLowerCase()
      .replace(/[-_\s]/g, "");

    if (normalized === "active") {
      return styles.damageStatusTagActive;
    }
    if (normalized === "completed" || normalized === "complete") {
      return styles.damageStatusTagCompleted;
    }
    if (normalized === "inactive" || normalized === "closed") {
      return styles.damageStatusTagInactive;
    }
    return styles.damageStatusTagDefault;
  };

  // Return explicit background color for a status value
  const getStatusTagColor = (status: any) => {
    const normalized = String(status || "")
      .toLowerCase()
      .replace(/[-_\s]/g, "");

    if (normalized === "active") return "#E5A408";
    if (normalized === "completed" || normalized === "complete") return "#28A745";
    if (normalized === "inactive" || normalized === "closed") return "#6C757D";
    return "#DC3545";
  };

  // Get the status text color
  const getStatusTextColor = (_status: any) => {
    return "#FFFFFF";
  };

  // Find primary display value (Type of Damage)
  const typeOfDamageColumn = columns.find(
    (col) => col.columnKey === "typeOfDamage"
  );
  const typeOfDamageValue = typeOfDamageColumn?.renderCell
    ? typeOfDamageColumn.renderCell(item)
    : item[typeOfDamageColumn?.columnKey];

  // Find status column
  const statusColumn = columns.find((col) => col.columnKey === "status");
  let statusValue: any = item[statusColumn?.columnKey] || item.status || "Active";

  // Columns for main display (exclude status and typeOfDamage from content) - show key fields
  const filteredColumns = columns.filter(
    (col) => col.columnKey !== "status" && col.columnKey !== "typeOfDamage"
  );
  
  // Show most important fields first: aisle, bay, level, slotId, numberOfInstances, severity
  const priorityFields = ["aisle", "bay", "level", "slotId", "numberOfInstances", "severity"];
  const priorityColumns = priorityFields.map(field => 
    filteredColumns.find(col => col.columnKey === field)
  ).filter(Boolean);
  
  // Remaining columns (dates, comments, images)
  const remainingColumns = filteredColumns.filter(
    col => !priorityFields.includes(col.columnKey)
  );

  return (
    <View style={[styles.damageCardContainer, isDark && { backgroundColor: colors.background.elevated, shadowColor: "transparent" }]}>
      {/* Header: Primary value (Type of Damage) + Status tag */}
      <View style={styles.damageCardHeader}>
        <View style={styles.damageCardPrimaryValue}>
          <Text style={[styles.damageCardPrimaryText, isDark && { color: colors.text.inverse }]} numberOfLines={2}>
            {typeof typeOfDamageValue === "object" && typeOfDamageValue?.props?.children
              ? typeOfDamageValue.props.children
              : String(typeOfDamageValue || item.typeOfDamage || "-")}
          </Text>
        </View>

        <View
          style={[
            styles.damageStatusTag,
            getStatusTagStyle(statusValue),
            { backgroundColor: getStatusTagColor(statusValue) },
          ]}
        >
          <Text
            style={[
              styles.damageStatusTagText,
              { color: getStatusTextColor(statusValue) },
            ]}
          >
            {statusValue}
          </Text>
        </View>
      </View>

      {/* Content: Priority fields first */}
      <View style={styles.damageCardContent}>
        {priorityColumns.map((column, index) => {
          if (!column) return null;
          
          const value = column.renderCell
            ? column.renderCell(item)
            : item[column.columnKey];

          const displayValue = typeof value === "object" && value?.props?.children
            ? value.props.children
            : String(value || "-");

          return (
            <View key={`priority-${column.columnKey}-${index}`} style={styles.damageCardRow}>
              <Text style={[styles.damageCardRowText, isDark && { color: colors.text.primary }]}>
                <Text style={[styles.damageCardRowLabel, isDark && { color: colors.text.secondary }]}>
                  {column.header}:{" "}
                </Text>
                <Text style={[styles.damageCardRowValue, isDark && { color: colors.text.primary }]}>
                  {displayValue}
                </Text>
              </Text>
            </View>
          );
        })}

        {/* Additional fields */}
        {remainingColumns.map((column, index) => {
          const value = column.renderCell
            ? column.renderCell(item)
            : item[column.columnKey];

          let displayValue = typeof value === "object" && value?.props?.children
            ? value.props.children
            : String(value || "-");

          // Special handling for images column
          if (column.columnKey === "images") {
            displayValue = item.images ? "Yes" : "No";
          }

          return (
            <View key={`additional-${column.columnKey}-${index}`} style={styles.damageCardRow}>
              <Text style={[styles.damageCardRowText, isDark && { color: colors.text.primary }]}>
                <Text style={[styles.damageCardRowLabel, isDark && { color: colors.text.secondary }]}>
                  {column.header}:{" "}
                </Text>
                <Text style={[styles.damageCardRowValue, isDark && { color: colors.text.primary }]}>
                  {displayValue}
                </Text>
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#F5F9FF",
    paddingBottom: 50,
    flexGrow: 1,
    alignItems: "stretch",
    paddingHorizontal: Platform.OS === "web" ? 24 : 16,
  },
  innerContainer: {},
  loadCapacityHeader: {
    backgroundColor: "rgb(254, 90, 23)", // Orange color from PulseCheckScreen
  },
  regulatoryHeader: {
    backgroundColor: "#0F307F", // Regulatory Standards blue
  },
  header1text: {
    fontSize: 16,
  },
  contentBox: {
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 8,
  },
  instructionBanner: {
    backgroundColor: "#004F9F",
    color: "white",
    textAlign: "center",
    padding: 12,
    borderRadius: 4,
    marginTop: 16,
    marginBottom: 16,
    fontWeight: "500",
  },
  // Action styles removed - now using RackingInspectionActionButtons component
  buttonTextDisabled: {
    opacity: 0.8,
  },
  sectionContainer: {
    marginBottom: 24,
  },
  footer: {
    textAlign: "center",
    marginTop: 40,
    color: "#868e96",
    fontSize: 12,
  },
  damageSection: {
    // marginTop: 4,
    marginBottom: 16,
    padding: 16,
    backgroundColor: "white",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  damageButton: {
    backgroundColor: "#00cc18bc",
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 6,
    alignSelf: "flex-end",
    minWidth: 140,
    height: Platform.OS === "web" ? "100%" : undefined,
    marginTop: -4,
  },
  damageButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "500",
  },
  damageText: {
    marginTop: 12,
    fontSize: 16,
    color: "#34495e",
    lineHeight: 24,
    backgroundColor: "#f8f9fa",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  damageFormContainer: {
    marginTop: 16,
    backgroundColor: "#f8f9fa",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  formInputsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    justifyContent: "space-between",
    marginBottom: 20,
  },
  inputGroup: {
    flex: 1,
    minWidth: 250,
    maxWidth: 300,
    marginBottom: 16,
  },
  labelColumn: {
    marginBottom: 8,
  },
  inputColumn: {
    width: "100%",
  },
  damageFormLabel: {
    backgroundColor: "#0066cc",
    fontSize: 14,
    fontWeight: "600",
    color: "white",
    padding: 8,
    borderRadius: 4,
    textAlign: "center",
    letterSpacing: 0.5,
  },
  damageFormInput: {
    fontSize: 15,
    padding: 12,
    backgroundColor: "white",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#ced4da",
    width: "100%",
    height: 44,
  },
  standardHeaderText: {
    fontWeight: "bold",
    color: "#0066cc",
    fontSize: 16,
  },
  contentCard1: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: Platform.select({
      web: 32,
      default: 20,
    }),
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginBottom: 16,
    position: "relative",
    zIndex: 1,
  },
  headerText: {
    fontWeight: "600",
    color: "#0066cc",
    fontSize: 16,
    textAlign: "center" as const,
  },
  section: {
    marginBottom: 20,
    paddingBottom: 0,
    borderBottomWidth: Platform.select({
      web: 0,
      default: 1,
    }),
    borderBottomColor: "#eee",
  },
  standardRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  standardContentContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cameraIconContainer: {
    padding: 8,
    marginLeft: 8,
  },
  standardContainer: {
    flex: 2,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: "#e9ecef",
    marginRight: 8,
    minHeight: 100,
  },
  standardText: {
    fontSize: 15,
    lineHeight: 22,
    color: "#34495e",
    textAlign: "left",
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: "#495057",
    padding: 12,
    backgroundColor: "white",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#ced4da",
    minHeight: 100,
  },
  numberInput: {
    textAlign: "center",
  },
  // Modify these containers to better accommodate inputs
  commentsContainer: {
    flex: 1,
    marginHorizontal: 4,
    minHeight: 100,
  },
  instancesContainer: {
    flex: 0.3,
    marginHorizontal: 4,
    minHeight: 100,
    justifyContent: "center",
  },
  tableAndButtonsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 0,
    gap: 20,
  },
  tableSection: {
    flex: 2,
    marginRight: 20,
  },
  headerRow2: {
    flexDirection: "row",
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    paddingBottom: 8,
    backgroundColor: "#f8f9fa",
    padding: 12,
    borderRadius: 4,
    alignItems: "center",
  },
  fullHeaderCell: {
    flex: 1,
    textAlign: "center",
    padding: 10,
    backgroundColor: "#77ab59",
    color: "#fff",
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  cellTitle: {
    width: "30%",
    padding: 8,
    borderRightWidth: 1,
    borderColor: "#000",
    fontWeight: "bold",
  },
  cellDesc: {
    width: "70%",
    padding: 8,
  },
  submitButtonRow: {
    flex: 1,
    flexDirection: "column",
    gap: 12,
    justifyContent: "flex-start",
    maxWidth: 400,
  },
  cancelButton: {
    width: "100%",
    minWidth: 350,
    backgroundColor: "#dc3545",
  },
  submitButton: {
    width: "100%",
    minWidth: 350,
    backgroundColor: "#008242",
  },
  datePickerWrapper: {
    paddingTop: 0,
    flex: 0.5,
    backgroundColor: "#ffffff",
    marginHorizontal: 4,
    minHeight: 60,
    justifyContent: "center",
    padding: 8,
    // borderRadius: 4,
    // borderWidth: 1,
    // borderColor: '#ced4da',
    position: "relative",
    zIndex: 1,
  },
  contentCard: {
    backgroundColor: "white",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginBottom: 16,
    position: "relative",
    zIndex: 1,
    overflow: "hidden",
    maxWidth: "100%",
  },
  contentCardTable: {
    overflow: "hidden",
    maxWidth: "100%",
  },
  title: {
    borderRadius: 4,
    backgroundColor: "#0066cc",
    padding: 12,
    fontSize: 20,
    fontWeight: "bold",
    color: "white",
    marginBottom: "1%",
    textAlign: "center",
  },
  damageHeaderContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1%",
    width: "100%",
  },
  damageTitle: {
    borderRadius: 6,
    backgroundColor: "#0066cc",
    padding: 12,
    fontSize: 20,
    fontWeight: "bold",
    color: "white",
    textAlign: "center",
  },

  scrollContainer: {
    flex: 1,
    width: "100%",
  },
  tableContainer: {
    minWidth: "100%",
  },
  successMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#E8F5E9",
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#4CAF50",
  },
  successIcon: {
    marginRight: 12,
  },
  successMessageText: {
    flex: 1,
    fontSize: 16,
    color: "#2E7D32",
  },
  dismissButton: {
    padding: 4,
  },
  loadingContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 16,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: "#0066CC",
  },
  exportButton: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
    flex: 1,
  },
  scrollToTopButton: {
    position: "absolute",
    bottom: Platform.OS === "web" ? 30 : 80,
    right: 20,
    zIndex: 1000,
  },
  scrollToTopTouchable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 28,
    width: 56,
    height: 56,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  arrowIcon: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  arrowText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0066CC",
    lineHeight: 24,
  },
  // Signature styles
  signaturePadWrapper: {
    marginBottom: 0,
    backgroundColor: "white",
    padding: 16,
  },
  signaturePadContainer: {
    padding: 10,
  },
  signatureText: {
    marginTop: 8,
    fontSize: 14,
    color: "#495057",
    fontWeight: "500",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  statusContainer: {
    backgroundColor: "white",
    borderRadius: 6,
    padding: 12,
    marginTop: 20,
    borderTopWidth: 8,
    borderTopColor: "#f5f7fa",
    paddingTop: 16,
  },
  statusRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 6,
    width: "96%",
    marginLeft: 20,
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#4B4B4B",
    width: 100,
    fontFamily: FontFamily.regular,
  },
  statusValue: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: FontFamily.regular,
  },
  statusComplete: {
    color: "#28a745",
  },
  statusPending: {
    color: "#ffc107",
  },
  // Miscellaneous Observations styles (matching LeadershipWalkMiscellaneousObservation)
  cardContainer: {
    borderRadius: 6,
    // allow header to expand beyond original bounds when wrapping
    overflow: Platform.OS === "ios" ? "visible" : "hidden",
  },
  sectionHeader: {
    backgroundColor: "#3582E6",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    // use minHeight so header can expand to 2 lines when needed
    minHeight: 48,
  },
  sectionHeaderText: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
  miscellaneousObservationsContainer: {
    borderColor: "#e9ecef",
    borderWidth: 1,
    borderTopWidth: 0,
    borderRadius: 6,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    backgroundColor: "white",
    padding: 16,
  },
  seperateCardContainers: {
    marginTop: 24,
  },
  damageButtonContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 8,
  },
  // iOS Mobile Damage Card Styles
  damageCardsContainer: {
    flex: 1,
    backgroundColor: "transparent",
  },
  cardsListContainer: {
    backgroundColor: "transparent",
  },
  damageCardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow: "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
      },
    }),
  },
  damageCardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  damageCardPrimaryValue: {
    flex: 1,
    marginRight: 12,
  },
  damageCardPrimaryText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    fontWeight: "500",
    color: "#005CBC",
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  damageStatusTag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    minWidth: 50,
    alignItems: "center",
    justifyContent: "center",
  },
  damageStatusTagText: {
    fontSize: 12,
    fontWeight: "400",
    textAlign: "center",
    lineHeight: 18,
    fontFamily: FontFamily.regular,
  },
  damageStatusTagActive: {
    backgroundColor: "#E5A408",
  },
  damageStatusTagCompleted: {
    backgroundColor: "#28A745",
  },
  damageStatusTagInactive: {
    backgroundColor: "#6C757D",
  },
  damageStatusTagDefault: {
    backgroundColor: "#DC3545",
  },
  damageCardContent: {
    marginBottom: 8,
  },
  damageCardRow: {
    marginBottom: 4,
  },
  damageCardRowText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  damageCardRowLabel: {
    fontWeight: "700",
    fontSize: Platform.OS === "web" ? 12 : 12,
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  damageCardRowValue: {
    fontWeight: "400",
    fontSize: Platform.OS === "web" ? 14 : 12,
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
});

// Map backend/user data to table row format for LHDataTable
function formatDamageDataForTable(data: any[]) {
  if (!Array.isArray(data)) {
    return [];
  }

  // Helper function to format ISO date to MM-DD-YYYY
  const formatDate = (isoDate: string | undefined) => {
    if (!isoDate) {
      return "";
    }
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
      return isoDate;
    } // Return as is if invalid date
    return `${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
      .getDate()
      .toString()
      .padStart(2, "0")}-${date.getFullYear()}`;
  };

  return data.map((item) => ({
    aisle: item.AISLE ?? item.aisle ?? "",
    bay: item.BAY_NUM ?? item.bay ?? "",
    level: item.LEVEL ?? item.level ?? "",
    slotId: item.SLOT_ID ?? item.slotId ?? "",
    typeOfDamage: item.SHORT_FORM ?? item.typeOfDamage ?? "",
    numberOfInstances: item.INSTANCES ?? item.numberOfInstances ?? "",
    severity: item.SEVERITY ?? item.severity ?? "",
    obsRecDate: formatDate(
      item.WALK_DATE ??
        item.obsRecDate ??
        new Date().toISOString().split("T")[0]
    ),
    tgtDtOfRepair: formatDate(
      item.DOR ?? item.targetDate ?? item.tgtDtOfRepair ?? ""
    ),
    actDtOfRepair: formatDate(item.DORCOMPLETION ?? item.actDtOfRepair ?? ""),
    status:
      item.Status !== undefined
        ? typeof item.Status === "string"
          ? item.Status
          : item.Status
          ? "Completed"
          : "Active"
        : item.status ?? "",
    comments: item.COMMENTS ?? item.comments ?? "",
    images: item.IMG1 || item.IMG2 ? "image" : item.images ?? "",
  }));
}

// Helper function to merge and deduplicate damage data
function mergeDamageData(activeDamages: any[], currentSessionDamages: any[]) {
  // Format both arrays
  const formattedCurrentSession = formatDamageDataForTable(
    currentSessionDamages
  );
  const formattedActiveDamages = formatDamageDataForTable(activeDamages);

  // Put current session damages first (newly added damages) to appear at the top
  const combined = [...formattedCurrentSession, ...formattedActiveDamages];

  // Deduplicate based on a combination of aisle, bay, level, slotId, and typeOfDamage
  // Keep the first occurrence (which will be from currentSessionDamages if it exists)
  const seen = new Set();
  return combined.filter((item) => {
    const key = `${item.aisle}-${item.bay}-${item.level}-${item.slotId}-${item.typeOfDamage}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export default RackingInspectionBeginPage;
